import { createSlice } from '@reduxjs/toolkit';
import {
  MOCK_STOP_STATES,
  MOCK_STOP_PHOTOS,
  MOCK_STOP_NOTES,
  MOCK_DRAW_LIST_PHOTOS,
} from '../data/mockData';
import { 
  STATUS_STRING_TO_NUMERIC, 
  STATUS_API_VALUES, 
  PHLEBONLY_STRING_TO_NUMERIC,
  formatApiTime 
} from '../constants/status';
import { utcTimeToLocal } from '../Utils';

const PHLEBONLY_API_VALUES = { 
  1: 'arrived', 
  2: 'in_progress', 
  3: 'complete' 
};

const initialState = {
  facilities:     [],
  stopStates:     MOCK_STOP_STATES,
  stopPhotos:     MOCK_STOP_PHOTOS,
  stopNotes:      MOCK_STOP_NOTES,
  drawListPhotos: MOCK_DRAW_LIST_PHOTOS,
  stopTimelines:  {},
  specimenTypes:  [],
  selectedStopId: null,
  selectedStopRole: null,
  selectedDay:    '0',
};

function hasRealCounts(v) {
  if (!v) return false;
  if (Array.isArray(v))  return v.some((c) => Number(c.count ?? c.collected ?? 0) > 0);
  if (typeof v === 'object') return Object.values(v).some((n) => Number(n) > 0);
  return false;
}

const stopsSlice = createSlice({
  name: 'stops',
  initialState,
  reducers: {

    addFacility(state, action) {
      const routes = Array.isArray(action.payload)
        ? action.payload
        : action.payload ? [action.payload] : [];

      const prevStops = {};
      state.facilities.forEach((route) => {
        (route.stops || []).forEach((stop) => { prevStops[stop.id] = stop; });
      });

      state.facilities = routes;

      routes.forEach((route) => {
        (route.stops || []).forEach((stop) => {
          const prev = prevStops[stop.id];

          if (prev) {
            if (!hasRealCounts(stop.collectedCounts) && hasRealCounts(prev.collectedCounts)) {
              stop.collectedCounts = prev.collectedCounts;
            }
            const serverPhotoLen = stop.photos?.length ?? 0;
            const localPhotoLen  = prev.photos?.length ?? 0;
            if (localPhotoLen > serverPhotoLen) {
              stop.photos = prev.photos;
            }
            if (!stop.driverNotes && prev.driverNotes) {
              stop.driverNotes = prev.driverNotes;
            }
          }

          // ✅ phlebOnly uses its own string→numeric map
          const isPhlebOnly = stop.serviceType === 'phlebotomy_only';
          const numericStatus = (
            isPhlebOnly
              ? PHLEBONLY_STRING_TO_NUMERIC[stop.status]
              : STATUS_STRING_TO_NUMERIC[stop.status]
          ) ?? 0;
          state.stopStates[stop.id] = numericStatus;

          if (Array.isArray(stop.timeline) && stop.timeline.length > 0) {
            const tlMap = state.stopTimelines[stop.id] || {};
            stop.timeline.forEach((entry) => {
              // NOTE: do NOT mutate entry.time here. stop.timeline is passed
              // straight into <ActivityTimeline>, which does its own
              // UTC → local conversion at render time. Converting it here too
              // caused a double conversion (wrong time shown for admin/driver).
              const code = STATUS_STRING_TO_NUMERIC[entry.status] ?? PHLEBONLY_STRING_TO_NUMERIC[entry.status];
              if (code !== undefined) {
                tlMap[code] = formatApiTime(utcTimeToLocal(entry.time));
              }
            });
            state.stopTimelines[stop.id] = tlMap;
          }
        });
      });
    },

    advanceStatus(state, action) {
      const { stopId, newStatus, note, driverNotes, timestamp, timeMinutes, specimenDestination } = action.payload;
      state.stopStates[stopId] = newStatus;

      state.facilities.forEach((route) => {
        const stop = (route.stops || []).find((s) => s.id === stopId);
        if (!stop) return;

        const isPhlebOnly = stop.serviceType === 'phlebotomy_only';
        const apiStatusStr = isPhlebOnly 
          ? PHLEBONLY_API_VALUES[newStatus] 
          : STATUS_API_VALUES[newStatus];

        stop.status = apiStatusStr ?? stop.status;
        if (note        !== undefined) stop.notes       = note;
        if (driverNotes !== undefined) stop.driverNotes = driverNotes;

       if (apiStatusStr) {
          if (!Array.isArray(stop.timeline)) stop.timeline = [];
          stop.timeline = stop.timeline.filter((e) => e.status !== apiStatusStr);
          
          const phlebStatuses = ['arrived', 'in_progress', 'complete', 'in_transit'];
          const deliveredByPhleb = apiStatusStr === 'delivered' && specimenDestination === 'lab';
          const role = (phlebStatuses.includes(apiStatusStr) || deliveredByPhleb) ? 'phleb' : 'driver';
          stop.timeline.push({
            status: apiStatusStr,
            time:   timeMinutes ?? (new Date().getUTCHours() * 60 + new Date().getUTCMinutes()),
            role,
            userId: role === 'phleb' ? stop.phlebId : stop.driverId,
          });
        }
      });

      if (note !== undefined) state.stopNotes[stopId] = note;

      if (!state.stopTimelines[stopId]) state.stopTimelines[stopId] = {};
      state.stopTimelines[stopId][newStatus] =
        timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },

    updateStopData(state, action) {
      const { stopId, updates } = action.payload;
      state.facilities.forEach((route) => {
        const stop = (route.stops || []).find((s) => s.id === stopId);
        if (stop) Object.assign(stop, updates);
      });
    },

    addPhoto(state, action) {
      const { stopId, photo } = action.payload;
      if (!state.stopPhotos[stopId]) state.stopPhotos[stopId] = [];
      state.stopPhotos[stopId].push(photo);
    },

    setNote(state, action) {
      const { stopId, note } = action.payload;
      state.stopNotes[stopId] = note;
    },

    captureDrawList(state, action) {
      state.drawListPhotos[action.payload.stopId] = true;
    },

    setSpecimenTypes(state, action) {
      state.specimenTypes = action.payload;
    },

     selectStop(state, action) {
      const payload = action.payload;
      if (payload && typeof payload === 'object') {
        state.selectedStopId = payload.id;
        state.selectedStopRole = payload.actingRole || 'phleb';
      } else {
        state.selectedStopId = payload;
        state.selectedStopRole = 'phleb';
      }
    },

    // Jab phleb kisi "driver" role wale stop pe click kare —
    // yeh sirf role-flag set karta hai; actual stop data route slice mein hai
    setActingContext(state, action) {
      const { stopId, role } = action.payload;
      state.selectedStopId = role === 'phleb' ? stopId : null; // driver role ka data yahan store nahi hota
      state.selectedStopRole = role;
    },

    clearSelectedStop(state) {
      state.selectedStopId = null;
      state.selectedStopRole = null;   // ← yeh line add karo
    },
    setSelectedDay(state, action)  { state.selectedDay = action.payload; },
  },
});

export const {
  addFacility, setSpecimenTypes, advanceStatus, updateStopData,
  addPhoto, setNote, captureDrawList,
  selectStop, clearSelectedStop, setSelectedDay,setActingContext
} = stopsSlice.actions;

export default stopsSlice.reducer;